const { gMachineAppViews } = require("./machine_app_global_variables");
const { prerequisiteValidator } = require("./machine_app_prerequisite_validation");
const machine_app_implementation_loader = require("./models/machine_app_implementation_loader");
const machine_app_state = require("./models/machine_app_state");

/**
 * @typedef {import('./views/machine_app_view').MachineAppViewString} MachineAppViewString
 */

/**
 * Compute the validation status for a given MachineApp step
 * @param {MachineAppViewString} machineAppView
 * @param {Array<MachineAppViewString>} itemViews
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
export const isStepValid = (machineAppView, itemViews, machineAppLibrary) => {
  const prerequisiteValidation = prerequisiteValidator(machineAppLibrary.getType());
  if(!prerequisiteValidation.success) {
    return false;
  }

  return itemViews
    .slice(0, getIndexOfView(machineAppView, itemViews) + 1)
    .every(view => {
      return isViewValidationSuccessful(view, machineAppLibrary);
    });
};

/**
 * Checks to see if a given view is successfully validated
 * @param {MachineAppViewString} machineAppView
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
function isViewValidationSuccessful(machineAppView, machineAppLibrary) {
  const validators = machine_app_implementation_loader.instance.getValidators(machineAppLibrary.getType());

  switch (machineAppView) {
    case gMachineAppViews.machineParameters:
      return isMachineParameterSuccessful(validators.machineParameters, machineAppLibrary);
    case gMachineAppViews.globalAsset:
      return isGlobalAssetSuccessful(validators.globalAssetValidatorMap, machineAppLibrary);
    case gMachineAppViews.configuration:
      return isConfigurationSuccessful(validators.configuration, machineAppLibrary);
    case gMachineAppViews.application:
      return isApplicationSuccessful(machineAppLibrary);
    default:
      throw new Error(`Unknown view type ${machineAppView}`);
  }
}

/**
 * @param {Function} machineParameterValidator
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
export function isMachineParameterSuccessful(machineParameterValidator, machineAppLibrary) {
  const lInterface = machine_app_state.getInterface();
  return machineParameterValidator(machineAppLibrary.getMachineParameters(), lInterface).success;
}

/**
 * @param {Object} validatorMap
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
function isGlobalAssetSuccessful(validatorMap, machineAppLibrary) {
  return Object.keys(validatorMap)
    .map(assetType => {
      const globalAssetListByType = machineAppLibrary.listGlobalAssetsByAssetType(assetType);
      const lValidGlobalAssetListByType = globalAssetListByType.filter(globalAsset => {
        return validateGlobalAssetItem(validatorMap, assetType, globalAsset);
      });
      return lValidGlobalAssetListByType.length != 0;
    })
    .every(validation => validation === true);
}

export function validateGlobalAssetItem(validatorMap, assetType, item) {
  const machineAppInterface = machine_app_state.getInterface();
  const validationFunction = validatorMap[assetType];

  if(assetType === 'default'){
    return validationFunction().success;
  }
  return validationFunction(item, machineAppInterface).success;
}


/**
 * @param {Function} validators
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
function isConfigurationSuccessful(configurationValidator, machineAppLibrary) {
  const lConfigurationList = machineAppLibrary.listConfigurations();
  const lValidConfigList = lConfigurationList.filter(lConfig => {
    const validation = configurationValidator(lConfig);
    return validation.success;
  });
  return lValidConfigList.length != 0;
}

/**
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
function isApplicationSuccessful(machineAppLibrary) {
  return machineAppLibrary.listApplications().length != 0;
}

/**
 * @param {MachineAppViewString} machineAppView
 * @param {Array<MachineAppViewString>} itemViews
 * @param {any} machineAppLibrary
 * @returns {boolean}
 */
export function canOpenView(machineAppView, itemViews, machineAppLibrary) {
  //we can always go to the first view
  if (getIndexOfView(machineAppView, itemViews) === 0) return true;
  const previousView = getPreviousView(machineAppView, itemViews);
  return isStepValid(previousView, itemViews, machineAppLibrary);
}

/**
 * @param {MachineAppViewString} machineAppView
 * @param {Array<MachineAppViewString>} itemViews
 * @returns {MachineAppViewString}
 */
export function getPreviousView(machineAppView, itemViews) {
  const viewIndex = getIndexOfView(machineAppView, itemViews);
  if (viewIndex === 0) return machineAppView;
  return itemViews[viewIndex - 1];
}

/**
 * @param {MachineAppViewString} machineAppView
 * @param {Array<MachineAppViewString>} itemViews
 * @returns {Number}
 */
function getIndexOfView(machineAppView, itemViews) {
  return itemViews.findIndex(view => view === machineAppView);
}
